import { FetchAnalyticsGraphParams } from '@services/AnalyticsGraph'
import dayjs from 'dayjs'

const generateFakeGraphData = (
  entity: FetchAnalyticsGraphParams['entity'],
  kind: FetchAnalyticsGraphParams['kind'],
  _count: number = 30
) => {
  console.log('Generating fake data for kind:', kind)
  // Here you can customize the fake data count based on the 'kind' parameter
  let count = _count
  if (kind === 'booking-conversion') {
    count = 5
  } else if (kind === 'visits-spend') {
    count = 10
  } else if (kind === 'revenue-party-size-spending-pattern') {
    count = 9
  }

  const startDate = dayjs().subtract(count, 'days')

  if (kind === 'platforms-registration') {
    return {
      usersCount: Math.floor(Math.random() * 1000) + 100,
      registrations: Array.from({ length: count }).map((_, i) => ({
        date: startDate.add(i, 'days').format('YYYY-MM-DD'),
        ios: Math.floor(Math.random() * 100) + 10,
        android: Math.floor(Math.random() * 100) + 10,
        whatsapp: Math.floor(Math.random() * 100) + 10,
      })),
    }
  } else if (kind === 'app-average-session-duration') {
    return [
      {
        label: '1-15 sec',
        fill: '#8f9a8c',
        count: Math.floor(Math.random() * 100),
      },
      {
        label: '16-30 sec',
        fill: '#adb7b9',
        count: Math.floor(Math.random() * 100),
      },
      {
        label: '31-60 sec',
        fill: '#bfbbb0',
        count: Math.floor(Math.random() * 100),
      },
      {
        label: '1-5 min',
        fill: '#b5a9b8',
        count: Math.floor(Math.random() * 100),
      },
      {
        label: '5+ min',
        fill: '#9a8474',
        count: Math.floor(Math.random() * 100),
      },
    ]
  } else if (kind === 'impression-openings') {
    return {
      openings: Array.from({ length: count }).map((_, i) => ({
        label: startDate.add(i, 'days').format('YYYY-MM-DD'),
        count: Math.floor(Math.random() * 100) + 10,
      })),
      notificationPush: Array.from({ length: 0 }).map(() => ({
        date: startDate.add(2, 'days').format('YYYY-MM-DD'),
      })),
      featuredVenue: Array.from({ length: 0 }).map(() => {
        const _start = startDate.add(3, 'days')
        return {
          start: _start.format('YYYY-MM-DD'),
          end: _start.add(1, 'days').format('YYYY-MM-DD'),
        }
      }),
    }
  } else if (kind === 'booking-conversion' && entity === 'venue') {
    return [
      {
        id: 0,
        name: 'homepage',
        count: 1055,
      },
      {
        id: 1,
        name: 'venue-book-pressed',
        count: 16230,
      },
      {
        id: 2,
        name: 'parameters',
        count: 16230,
      },
      {
        id: 3,
        name: 'slots',
        count: 13544,
      },
      {
        id: 4,
        name: 'confirm',
        count: 2682,
      },
      {
        id: 5,
        name: 'loading',
        count: 3098,
      },
      {
        id: 6,
        name: 'success',
        count: 1411,
      },
      {
        id: 7,
        name: 'failure',
        count: 400,
      },
    ]
  } else if (kind === 'booking-conversion-heatmap' && entity === 'venue') {
    const partySizes = [2, 3, 4, 5, 6, 7, 8]
    const daysOfWeek = [1, 2, 3, 4, 5, 6, 7]

    return partySizes.flatMap((partySize) =>
      daysOfWeek.map((dayOfWeek) => {
        const search = Math.floor(Math.random() * 40) + 5
        const booking = Math.floor(Math.random() * (search + 1))

        return { partySize, dayOfWeek, search, booking }
      })
    )
  } else if (kind === 'plan-repartition' && entity === 'membership') {
    // Real shape: { label, value (count), color (API palette), percent }.
    return [
      { label: 'Couple', value: 720, color: '#8884d8', percent: 61 },
      { label: 'Single', value: 205, color: '#83a6ed', percent: 17 },
      { label: '-33 years', value: 260, color: '#8dd1e1', percent: 22 },
    ]
  } else if (kind === 'discount-repartition' && entity === 'membership') {
    // Real shape: ONE slice PER COUPON CODE (label = code, value = number of
    // subscriptions, colours cycling through the API's 10-colour palette,
    // integer percents summing to 100). ~40 coupons: one at 24%, two at 5%,
    // the rest at 1-2%.
    const palette = [
      '#8884d8',
      '#83a6ed',
      '#8dd1e1',
      '#82ca9d',
      '#a4de6c',
      '#d0ed57',
      '#ffc658',
      '#ff8042',
      '#ff6699',
      '#c471ed',
    ]
    const codes = [
      'DESIGNMIAMI26',
      'ABMAITREJEAN',
      'JON385',
      'WELCOME10',
      'FOUNDER',
      'SUMMER25',
      'FRIENDS',
      'PARIS2026',
      'VIP100',
      'NEWYEAR',
      'BDAY',
      'LYONLOVE',
      'CHEF50',
      'MIAMI24',
      'TEAM7',
      'ROOFTOP',
      'GALA26',
      'PRESS',
      'ALUMNI',
      'BETA',
      'SPRING',
      'AUTUMN',
      'WINTER',
      'LAUNCH',
      'PARTNER1',
      'PARTNER2',
      'PARTNER3',
      'STUDENT',
      'COUPLE5',
      'ENZO10',
      'MARIE20',
      'SOPHIE15',
      'LUC30',
      'ANNA12',
      'TOM9',
      'LEA8',
      'NOA7',
      'IRIS6',
      'MAYA4',
      'ZOE3',
    ]
    const percents = [
      24,
      5,
      5,
      ...Array.from({ length: 37 }, (_, i) => (i < 29 ? 2 : 1)),
    ]
    return codes.map((label, index) => ({
      label,
      value: percents[index] * 4,
      color: palette[index % palette.length],
      percent: percents[index],
    }))
  } else if (kind === 'membership-monthly-revenue' && entity === 'membership') {
    const now = new Date()
    // The real answer is an envelope: currency at the root, months under `data`.
    return {
      currency: { isoCode: 'EUR', symbol: '€' },
      data: Array.from({ length: 12 }, (_, index) => {
        const date = new Date(
          now.getFullYear(),
          now.getMonth() - (11 - index),
          1
        )
        const month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
        return {
          month,
          collected: 400000 + index * 55000 + ((index * 37) % 5) * 20000,
          activeSubscriptions: 1100 + index * 23,
          partial: index === 11,
        }
      }),
    }
  } else if (kind === 'upcoming-renewals' && entity === 'membership') {
    return [
      { key: '0-30d', label: '0-30d', count: 112, amount: 435700 },
      { key: '31-60d', label: '31-60d', count: 96, amount: 384000 },
      { key: '61-90d', label: '61-90d', count: 128, amount: 512000 },
    ]
  } else if (kind === 'revenue-weekly-spend-trends') {
    return [
      {
        name: 'Monday',
        spendPerReservation: Math.floor(Math.random() * 100000),
        spendPerPerson: Math.floor(Math.random() * 100000),
      },
      {
        name: 'Tuesday',
        spendPerReservation: Math.floor(Math.random() * 100000),
        spendPerPerson: Math.floor(Math.random() * 100000),
      },
      {
        name: 'Wednesday',
        spendPerReservation: Math.floor(Math.random() * 100000),
        spendPerPerson: Math.floor(Math.random() * 100000),
      },
      {
        name: 'Thursday',
        spendPerReservation: Math.floor(Math.random() * 100000),
        spendPerPerson: Math.floor(Math.random() * 100000),
      },
      {
        name: 'Friday',
        spendPerReservation: Math.floor(Math.random() * 100000),
        spendPerPerson: Math.floor(Math.random() * 100000),
      },
      {
        name: 'Saturday',
        spendPerReservation: Math.floor(Math.random() * 100000),
        spendPerPerson: Math.floor(Math.random() * 100000),
      },
      {
        name: 'Sunday',
        spendPerReservation: Math.floor(Math.random() * 100000),
        spendPerPerson: Math.floor(Math.random() * 100000),
      },
    ]
  }

  return Array.from({ length: count }, (_, i) => {
    const date = startDate.add(i, 'days').format('YYYY-MM-DD')

    // Generate different data structures based on kind
    switch (kind) {
      case 'event-opening':
      case 'daily-active-users':
      case 'average-session-duration-per-user':
      case 'average-session-duration-for-user':
        return {
          label: date,
          count: Math.floor(Math.random() * 800) + 50,
        }
      case 'app-screens-most-viewed':
        return {
          label: `Screen Name${i}`,
          count: Math.floor(Math.random() * 800) + 50,
        }
      case 'booking-conversion':
        // Entity venue is handled above so here we handle only user
        return {
          name: `Screen ${i + 1}`,
          fill: `#${Math.floor(Math.random() * 16777215)
            .toString(16)
            .padStart(6, '0')}`,
          value: Math.floor(Math.random() * 1000) + 100,
        }

      case 'revenue-party-size-spending-pattern':
        return {
          partySize: i + 1,
          spendPerReservation: Math.floor(Math.random() * 20000) + 50,
          spendPerPerson: Math.floor(Math.random() * 5000) + 5,
        }
      case 'visits-spend':
        return {
          name: `Venue ${i + 1}`,
          count: Math.floor(Math.random() * 50) + 1,
          spend: Math.floor(Math.random() * 5000) + 500,
        }
      case 'global-venues-opening-history':
        return {
          venueName: `Venue ${i + 1}`,
          venueSlug: `venue-${i + 1}`,
          count: Math.floor(Math.random() * 1000) + 44,
        }
      default:
        console.warn('No fake data generator for kind:', kind)
        return {
          date,
          value: Math.floor(Math.random() * 1000) + 100,
          count: Math.floor(Math.random() * 100) + 10,
        }
    }
  })
}

export default generateFakeGraphData
